import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../../../../js/authToken';
import API_BASE_URL from '../../../../js/urlHelper';
import FetchWithGif from '../../../../components/Reutilizables/FetchWithGif';
import ComprobanteModal from './ComprobanteModal';
import CapturaAbonoModal from './CapturaAbonoModal';
import ReportePrestamosTotales from './ReportePrestamosTotales';
import DetallePrestamoCuotas from './DetallePrestamoCuotas';
import ComprobantesParcialesModal from '../EstadosComponents/components/modals/ComprobantesParcialesModal';
import { format, parseISO } from 'date-fns';

const ITEMS_PER_PAGE = 4;

const FiltrarPrestamos = () => {
  const [isGroupSearch, setIsGroupSearch] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedLoan, setSelectedLoan] = useState('');
  const [estado, setEstado] = useState('');
  const [selectedAsesor, setSelectedAsesor] = useState('');
  const [groups, setGroups] = useState([]);
  const [clients, setClients] = useState([]);
  const [loans, setLoans] = useState([]);
  const [asesores, setAsesores] = useState([]);
  const [cuotas, setCuotas] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAdvisors, setLoadingAdvisors] = useState(false);
  const [clientPage, setClientPage] = useState(1);
  const [loanPage, setLoanPage] = useState(1);
  const [clientSearch, setClientSearch] = useState('');
  const [filterClientsWithLoans, setFilterClientsWithLoans] = useState(false);
  const [showLoadingGif, setShowLoadingGif] = useState(false);
  const [showComprobanteModal, setShowComprobanteModal] = useState(false);
  const [selectedCuotaId, setSelectedCuotaId] = useState(null);
  const [comprobanteData, setComprobanteData] = useState({ comprobanteUrl: '' });
  const [comprobanteError, setComprobanteError] = useState('');
  const [loadingComprobante, setLoadingComprobante] = useState(false);
  const [showComprobantesParcialesModal, setShowComprobantesParcialesModal] = useState(false);
  const [pagosMultiples, setPagosMultiples] = useState({ cuota: null, pagos: [] });
  const [showCapturaAbonoModal, setShowCapturaAbonoModal] = useState(false);
  const [selectedLoanForAbono, setSelectedLoanForAbono] = useState(null);
  const [selectedClientForAbono, setSelectedClientForAbono] = useState(null);
  const [showReportViewer, setShowReportViewer] = useState(false);
  const [reportType, setReportType] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchGroups();
    fetchAdvisors();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/groups`);
      const data = await response.json();
      setGroups(data);
    } catch (err) {
      setError('Error al cargar los grupos');
    }
  };

  const fetchAdvisors = async () => {
    try {
      setLoadingAdvisors(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/advisors`);
      const data = await response.json();
      setAsesores(data);
    } catch (err) {
      setError('Error al cargar los asesores');
    } finally {
      setLoadingAdvisors(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (isGroupSearch && selectedGroup) {
        fetchClients({ group_id: selectedGroup, search: clientSearch, with_loans: filterClientsWithLoans });
      } else if (!isGroupSearch && selectedAsesor) {
        fetchClients({ advisor_id: selectedAsesor, search: clientSearch, with_loans: filterClientsWithLoans });
      } else if (!isGroupSearch && query) {
        fetchClients({ search: query, with_loans: filterClientsWithLoans });
      } else {
        setClients([]);
        setSelectedClient('');
        setLoans([]);
        setSelectedLoan('');
        setCuotas([]);
        setClientPage(1);
        setFilterClientsWithLoans(false);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [isGroupSearch, selectedGroup, selectedAsesor, clientSearch, filterClientsWithLoans, query]);

  const fetchClients = async (params) => {
    try {
      setLoading(true);
      setShowLoadingGif(true);
      const queryString = new URLSearchParams(params).toString();
      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/clients?${queryString}`);
      const data = await response.json();
      setClients(data);
      setSelectedClient('');
      setLoans([]);
      setSelectedLoan('');
      setCuotas([]);
      setClientPage(1);
    } catch (err) {
      setError('Error al cargar los clientes');
    } finally {
      setLoading(false);
      setShowLoadingGif(false);
    }
  };

  useEffect(() => {
    if (selectedClient) {
      fetchLoans({ client_id: selectedClient, page: loanPage });
    } else {
      setLoans([]);
      setSelectedLoan('');
      setCuotas([]);
      setLoanPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient, loanPage]);

  const fetchLoans = async (params) => {
    try {
      setLoading(true);
      setShowLoadingGif(true);
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v !== '')
      );
      if (selectedAsesor) {
        cleanParams.advisor_id = selectedAsesor;
      }
      const queryString = new URLSearchParams(cleanParams).toString();
      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/loans?${queryString}`);
      const data = await response.json();
      setLoans(data.data || []);
      setSelectedLoan('');
      setCuotas([]);
    } catch (err) {
      setError('Error al cargar los préstamos');
    } finally {
      setLoading(false);
      setShowLoadingGif(false);
    }
  };

  useEffect(() => {
    if (selectedLoan) {
      handleFilter();
    } else {
      setCuotas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLoan, estado]);

  const handleFilter = async () => {
    if (!selectedLoan) {
      setCuotas([]);
      return;
    }

    try {
      setLoading(true);
      setShowLoadingGif(true);
      const params = { loan_id: selectedLoan };
      if (estado) params.estado = estado;

      const queryString = new URLSearchParams(params).toString();
      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/installments?${queryString}`);
      const data = await response.json();
      setCuotas(data);
      setError('');
    } catch (err) {
      setError('Error al filtrar las cuotas');
    } finally {
      setLoading(false);
      setShowLoadingGif(false);
    }
  };

  const ensureHttpsUrl = (url) => {
    if (!url) return url;
    if (url.includes('localhost') || url.includes('127.0.0.1')) {
      return url;
    }
    return url.replace(/^http:/, 'https:');
  };

  const fetchComprobante = async (idCuota) => {
    try {
      setLoadingComprobante(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/pagos/comprobante/${idCuota}`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Error al obtener el comprobante');
      }
      const comprobanteUrl = data.comprobante_url ? ensureHttpsUrl(data.comprobante_url) : '';
      console.log('Original comprobante URL:', data.comprobante_url);
      console.log('Transformed comprobante URL:', comprobanteUrl);
      setComprobanteData({ comprobanteUrl });
      setComprobanteError('');
    } catch (err) {
      setComprobanteError(err.message || 'Error al obtener el comprobante');
      setComprobanteData({ comprobanteUrl: '' });
    } finally {
      setLoadingComprobante(false);
    }
  };

  const handleViewComprobante = (cuotaId) => {
    const cuota = cuotas.find(c => c.idCuota === cuotaId);
    if (!cuota) return;
    const pagosAplicados = Array.isArray(cuota.pagos) ? cuota.pagos : [];

    if (pagosAplicados.length > 0) {
      handleViewComprobantesMultiples(cuota, pagosAplicados);
      return;
    }

    setSelectedCuotaId(cuotaId);
    setShowComprobanteModal(true);
    fetchComprobante(cuotaId);
  };

  const handleViewComprobantesMultiples = async (cuota, pagosAplicados) => {
    setSelectedCuotaId(cuota?.idCuota || null);
    setShowComprobantesParcialesModal(true);
    setPagosMultiples({ cuota, pagos: [] });
    setLoadingComprobante(true);

    try {
      const pagosConComprobantes = await Promise.all(
        pagosAplicados.map(async (pago) => {
          try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/pagos/comprobante-por-pago/${pago.idPago}`);
            const data = await response.json();
            return {
              ...pago,
              comprobante_url: response.ok && data.comprobante_url ? ensureHttpsUrl(data.comprobante_url) : null
            };
          } catch (error) {
            console.error(`Error al obtener comprobante del pago ${pago.idPago}:`, error);
            return { ...pago, comprobante_url: null };
          }
        })
      );

      setPagosMultiples({ cuota, pagos: pagosConComprobantes });
    } finally {
      setLoadingComprobante(false);
    }
  };

  const handleViewAbono = (loanId, clientId) => {
    setSelectedLoanForAbono(loanId);
    setSelectedClientForAbono(clientId);
    setShowCapturaAbonoModal(true);
  };

  const handleClearFilters = () => {
    setIsGroupSearch(false);
    setQuery('');
    setSelectedGroup('');
    setSelectedClient('');
    setSelectedLoan('');
    setEstado('');
    setSelectedAsesor('');
    setClients([]);
    setLoans([]);
    setCuotas([]);
    setError('');
    setClientPage(1);
    setLoanPage(1);
    setClientSearch('');
    setFilterClientsWithLoans(false);
    setShowComprobanteModal(false);
    setShowComprobantesParcialesModal(false);
    setPagosMultiples({ cuota: null, pagos: [] });
    setShowCapturaAbonoModal(false);
    setShowReportViewer(false);
    setReportType('');
    setShowModal(false);
    setPdfUrl(null);
  };

  const handleClientClick = (clientId) => {
    if (selectedClient === clientId) {
      setSelectedClient('');
      setLoans([]);
      setSelectedLoan('');
      setCuotas([]);
    } else {
      setSelectedClient(clientId);
      setSelectedLoan('');
      setCuotas([]);
      setLoanPage(1);
    }
  };

  const handleLoanClick = (loanId) => {
    if (selectedLoan !== loanId) {
      setSelectedLoan(loanId);
    }
  };

  const getStatusStyles = (estado) => {
    switch ((estado || '').toLowerCase()) {
      case 'pendiente': return 'bg-yellow-100 text-yellow-800';
      case 'pagado': return 'bg-green-100 text-green-800';
      case 'vence_hoy': return 'bg-orange-100 text-orange-800';
      case 'vencido': return 'bg-red-100 text-red-800';
      case 'prepagado': return 'bg-blue-100 text-blue-800';
      case 'cancelado': return 'bg-gray-300 text-gray-900';
      case 'refinanciado': return 'bg-purple-100 text-purple-800';
      case 'mora': return 'bg-red-200 text-red-900';
      case 'reprogramado': return 'bg-indigo-100 text-indigo-800';
      case 'activo': return 'bg-teal-100 text-teal-800';
      case 'vigente': return 'bg-emerald-100 text-emerald-800';
      case 'liquidado': return 'bg-gray-100 text-gray-700 line-through';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleGenerateLoansPDF = () => {
    setReportType('loans');
    setShowReportViewer(true);
  };

  const handleGenerateInstallmentsPDF = () => {
    setReportType('installments');
    setShowReportViewer(true);
  };

  const handlePDFGenerated = (url) => {
    setPdfUrl(url);
    setShowModal(true);
  };

  const handlePDFComplete = () => {
    setShowReportViewer(false);
  };

  const closeModal = () => {
    setShowModal(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const paginatedClients = clients.slice(
    (clientPage - 1) * ITEMS_PER_PAGE,
    clientPage * ITEMS_PER_PAGE
  );
  const totalClientPages = Math.ceil(clients.length / ITEMS_PER_PAGE);
  const paginatedLoans = loans.slice(
    (loanPage - 1) * ITEMS_PER_PAGE,
    loanPage * ITEMS_PER_PAGE
  );
  const totalLoanPages = Math.ceil(loans.length / ITEMS_PER_PAGE);

  const formatPaymentDate = (cuota) => {
    if (!cuota) return '-';

    const montoCuota = Number(cuota.monto) || 0;
    const moraAplicada = Number(cuota.mora) || 0;
    const montoObjetivo = montoCuota + moraAplicada;
    const montoPagado = Number(cuota.monto_pagado_total) || 0;
    const pagoCompleto = cuota.pago_completo || (cuota.estado === 'pagado' && montoPagado + 0.01 >= montoObjetivo);

    if (pagoCompleto && cuota.fecha_pago_total) {
      return formatDate(cuota.fecha_pago_total);
    }

    return '-';
  };

  const labelClass = 'mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-600';
  const inputClass = 'w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200';
  const selectClass = inputClass;
  const primaryButtonClass = 'w-full md:w-auto rounded-xl bg-red-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400';
  const secondaryButtonClass = 'w-full md:w-auto rounded-xl border border-red-500 bg-white px-6 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50';
  const paginationButtonClass = 'rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <div className="space-y-6">
      {showLoadingGif && <FetchWithGif />}
      <section className="ui-card relative overflow-hidden px-6 py-7 md:px-8">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_88%_10%,rgba(217,44,52,0.12),transparent_38%)]" />
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-red-600">Control de cartera</p>
          <h2 className="mt-2 text-2xl font-semibold text-slate-900 md:text-3xl">Historial de préstamos</h2>
          <p className="mt-2 text-sm text-slate-600">
            Consulta clientes, préstamos y cuotas en una sola vista con filtros por grupo, asesor y estado.
          </p>
        </div>
      </section>

      <ComprobanteModal
        show={showComprobanteModal}
        onClose={() => {
          setShowComprobanteModal(false);
          setSelectedCuotaId(null);
          setComprobanteData({ comprobanteUrl: '' });
          setComprobanteError('');
          setLoadingComprobante(false);
        }}
        modalTitle="Comprobante de Pago"
        modalContent={comprobanteData}
        modalError={comprobanteError}
        selectedCuota={cuotas.find(cuota => cuota.idCuota === selectedCuotaId)}
        handleConfirmarPagoPrepagado={() => { }}
        loadingComprobante={loadingComprobante}
      />

      <ComprobantesParcialesModal
        show={showComprobantesParcialesModal}
        onClose={() => {
          setShowComprobantesParcialesModal(false);
          setPagosMultiples({ cuota: null, pagos: [] });
          setSelectedCuotaId(null);
        }}
        cuota={pagosMultiples.cuota}
        prestamo={loans.find(loan => loan.idPrestamo === (pagosMultiples.cuota?.idPrestamo || selectedLoan))}
        pagos={pagosMultiples.pagos}
        loading={loadingComprobante}
      />

      <CapturaAbonoModal
        show={showCapturaAbonoModal}
        onClose={() => {
          setShowCapturaAbonoModal(false);
          setSelectedLoanForAbono(null);
          setSelectedClientForAbono(null);
        }}
        clienteId={selectedClientForAbono}
        prestamoId={selectedLoanForAbono}
        modalTitle="Captura de Abono"
      />

      {showReportViewer && reportType === 'loans' && (
        <ReportePrestamosTotales
          key={`report-loans-${Date.now()}`}
          loans={loans}
          selectedClient={selectedClient}
          clients={clients}
          asesores={asesores}
          selectedAsesor={selectedAsesor}
          onComplete={handlePDFComplete}
          onPDFGenerated={handlePDFGenerated}
        />
      )}

      {showReportViewer && reportType === 'installments' && (
        <DetallePrestamoCuotas
          key={`report-installments-${Date.now()}`}
          cuotas={cuotas}
          selectedClient={selectedClient}
          selectedLoan={selectedLoan}
          clients={clients}
          loans={loans}
          asesores={asesores}
          selectedAsesor={selectedAsesor}
          onComplete={handlePDFComplete}
          onPDFGenerated={handlePDFGenerated}
        />
      )}

      {showModal && pdfUrl && (
        <div className="modal-overlay">
          <div className="modal-panel modal-panel-lg flex h-[80vh] flex-col">
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <h2 className="text-lg font-semibold text-slate-900">
                Vista previa del {reportType === 'loans' ? 'reporte de préstamos' : 'reporte de cuotas'}
              </h2>
              <button
                onClick={closeModal}
                className="text-slate-500 transition hover:text-slate-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-4">
              <iframe src={pdfUrl} className="h-full w-full border-0" title="PDF Preview" />
            </div>
          </div>
        </div>
      )}

      <section className="ui-card p-6">
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="groupSearch"
            checked={isGroupSearch}
            onChange={() => {
              setIsGroupSearch(!isGroupSearch);
              setSelectedAsesor('');
              setSelectedGroup('');
              setClients([]);
              setSelectedClient('');
              setSelectedLoan('');
              setCuotas([]);
              setQuery('');
              setClientSearch('');
              setFilterClientsWithLoans(false);
            }}
            className="h-5 w-5 text-red-600 focus:ring-red-500 border-gray-300 rounded"
          />
          <label htmlFor="groupSearch" className="ml-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
            Buscar por grupo
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {isGroupSearch ? (
            <div className="md:col-span-2">
              <label className={labelClass}>
                SELECCIONAR GRUPO
              </label>
              <select
                value={selectedGroup}
                onChange={(e) => {
                  setSelectedGroup(e.target.value);
                  setSelectedAsesor('');
                  setSelectedClient('');
                  setSelectedLoan('');
                  setCuotas([]);
                  setClientPage(1);
                  setFilterClientsWithLoans(false);
                }}
                className={selectClass}
              >
                <option value="">SELECCIONE GRUPO</option>
                {groups.map(group => (
                  <option key={group.idGrupo} value={group.idGrupo}>
                    {group.nombre}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="md:col-span-2">
              <label className={labelClass}>
                BUSCAR CLIENTE (DNI, ID, NOMBRE O APELLIDO)
              </label>
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value.toUpperCase());
                  setSelectedClient('');
                  setSelectedLoan('');
                  setCuotas([]);
                  setLoanPage(1);
                }}
                placeholder="INGRESE DNI, ID, NOMBRE O APELLIDO"
                className={inputClass}
              />
            </div>
          )}
          <div>
            <label className={labelClass}>
              ESTADO
            </label>
            <select
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
              className={selectClass}
            >
              <option value="">TODOS</option>
              <option value="pendiente">PENDIENTE</option>
              <option value="pagado">PAGADO</option>
              <option value="vence_hoy">VENCE HOY</option>
              <option value="vencido">VENCIDO</option>
              <option value="prepagado">PREPAGADO</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>
              ASESOR
            </label>
            <select
              value={selectedAsesor}
              onChange={(e) => {
                setSelectedAsesor(e.target.value);
                setSelectedGroup('');
                setIsGroupSearch(false);
                setSelectedClient('');
                setSelectedLoan('');
                setCuotas([]);
                setClientPage(1);
                setFilterClientsWithLoans(false);
              }}
              disabled={loadingAdvisors}
              className={`${selectClass} ${loadingAdvisors ? 'cursor-not-allowed bg-gray-100' : ''
                }`}
            >
              {loadingAdvisors ? (
                <option value="">CARGANDO...</option>
              ) : (
                <>
                  <option value="">TODOS</option>
                  {asesores.map(asesor => (
                    <option key={asesor.id} value={asesor.id}>
                      {asesor.nombre} {asesor.apellidoPaterno} {asesor.apellidoMaterno}
                      {asesor.apellidoConyuge ? ` ${asesor.apellidoConyuge}` : ''}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
          <div className="md:self-end md:col-span-6 flex flex-col md:flex-row gap-4">
            <button
              onClick={handleFilter}
              disabled={(!selectedLoan || loading)}
              className={primaryButtonClass}
            >
              {loading ? 'Filtrando...' : 'Filtrar'}
            </button>
            <button
              type="button"
              onClick={handleClearFilters}
              className={secondaryButtonClass}
            >
              LIMPIAR FILTROS
            </button>
          </div>
        </div>
      </section>

      {(isGroupSearch || selectedAsesor || query) && (
        <section className="ui-card p-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <h3 className="text-xl font-semibold text-slate-900">CLIENTES</h3>
            <input
              type="text"
              value={clientSearch}
              onChange={(e) => {
                setClientSearch(e.target.value.toUpperCase());
                setClientPage(1);
              }}
              placeholder="Buscar cliente en esta página..."
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200 md:w-[22rem]"
            />
          </div>
          {paginatedClients.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {paginatedClients.map(client => (
                  <button
                    key={client.idUsuario}
                    onClick={() => handleClientClick(client.idUsuario)}
                    className={`rounded-xl border p-4 text-left transition focus:outline-none ${selectedClient === client.idUsuario
                      ? 'border-blue-300 bg-blue-50/60'
                      : 'border-slate-200 bg-white hover:border-blue-200 hover:bg-slate-50'
                      }`}
                  >
                    <h4 className="text-base font-semibold text-slate-900">
                      {client.nombre} {client.apellidoPaterno} {client.apellidoMaterno}
                    </h4>
                    <p className="mt-1 text-sm text-slate-600">DNI: {client.dni}</p>
                    <p className="mt-1 text-sm text-slate-600">
                      Promedio de mora: {Number(client.promedioMora || 0).toFixed(2)} días
                    </p>
                    <p className="mt-1 text-sm text-slate-600">
                      Préstamos: {client.prestamoCount}
                    </p>
                  </button>
                ))}
              </div>
              {totalClientPages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <button
                    onClick={() => setClientPage(prev => Math.max(prev - 1, 1))}
                    disabled={clientPage === 1}
                    className={paginationButtonClass}
                  >
                    Anterior
                  </button>
                  <span className="px-4 py-2 text-sm font-medium text-slate-600">
                    Página {clientPage} de {totalClientPages}
                  </span>
                  <button
                    onClick={() => setClientPage(prev => Math.min(prev + 1, totalClientPages))}
                    disabled={clientPage === totalClientPages}
                    className={paginationButtonClass}
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </>
          ) : (
            !loading && (
              <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-yellow-700">
                No se encontraron clientes para los filtros seleccionados.
              </div>
            )
          )}
        </section>
      )}

      {loans.length > 0 && (
        <section className="ui-card p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-slate-900">PRÉSTAMOS</h3>
            <button
              onClick={handleGenerateLoansPDF}
              disabled={!selectedClient}
              className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              Reporte Préstamos
            </button>
          </div>
          <div className="max-w-full overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-[1280px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                  <th className="py-3 px-4">ID PRÉSTAMO</th>
                  <th className="py-3 px-4">ABONADO POR</th>
                  <th className="py-3 px-4">CLIENTE</th>
                  <th className="py-3 px-4">MONTO</th>
                  <th className="py-3 px-4">TOTAL</th>
                  <th className="py-3 px-4">CUOTAS</th>
                  <th className="py-3 px-4">INTERÉS</th>
                  <th className="py-3 px-4">VALOR CUOTA</th>
                  <th className="py-3 px-4">FRECUENCIA</th>
                  <th className="py-3 px-4">FECHA INICIO</th>
                  <th className="py-3 px-4">ESTADO</th>
                  <th className="py-3 px-4">ASESOR</th>
                  <th className="py-3 px-4">ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {paginatedLoans.map(loan => (
                  <tr
                    key={loan.idPrestamo}
                    onClick={() => handleLoanClick(loan.idPrestamo)}
                    className={`cursor-pointer border-b border-slate-200 transition hover:bg-slate-50 ${selectedLoan === loan.idPrestamo ? 'bg-blue-50/60' : ''}`}
                    tabIndex={0}
                    role="button"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleLoanClick(loan.idPrestamo);
                      }
                    }}
                  >
                    <td className="py-3 px-4">{loan.idPrestamo}</td>
                    <td className="py-3 px-4">{loan.abonado_por || 'N/A'}</td>
                    <td className="py-3 px-4">{loan.cliente}</td>
                    <td className="py-3 px-4">S/ {parseFloat(loan.monto).toFixed(2)}</td>
                    <td className="py-3 px-4">S/ {parseFloat(loan.total).toFixed(2)}</td>
                    <td className="py-3 px-4">{loan.cuotas}</td>
                    <td className="py-3 px-4">{loan.interes}%</td>
                    <td className="py-3 px-4">S/ {parseFloat(loan.valor_cuota).toFixed(2)}</td>
                    <td className="py-3 px-4">{loan.frecuencia}</td>
                    <td className="py-3 px-4">{formatDate(loan.fecha_inicio)}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${getStatusStyles((loan.estado_actual || loan.estado || '').toLowerCase())}`}
                      >
                        {(loan.estado_actual || loan.estado || '').toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-4">{loan.asesor}</td>
                    <td className="py-3 px-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewAbono(loan.idPrestamo, selectedClient);
                        }}
                        className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-red-700"
                        aria-label="Ver captura de abono"
                      >
                        Ver Abono
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalLoanPages > 1 && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <button
                onClick={() => setLoanPage(prev => Math.max(prev - 1, 1))}
                disabled={loanPage === 1}
                className={paginationButtonClass}
              >
                Anterior
              </button>
              <span className="px-4 py-2 text-sm font-medium text-slate-600">
                Página {loanPage} de {totalLoanPages}
              </span>
              <button
                onClick={() => setLoanPage(prev => Math.min(prev + 1, totalLoanPages))}
                disabled={loanPage === totalLoanPages}
                className={paginationButtonClass}
              >
                Siguiente
              </button>
            </div>
          )}
        </section>
      )}

      {loading && (
        <div className="flex justify-center mb-6">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-red-600 border-t-transparent"></div>
        </div>
      )}

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
          {error}
        </div>
      )}

      {cuotas.length > 0 && (
        <section className="ui-card p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-slate-900">CUOTAS</h3>
            <div className="flex gap-2">
              <button
                onClick={handleGenerateInstallmentsPDF}
                disabled={!selectedLoan}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                Reporte Cuotas
              </button>
            </div>
          </div>
          <div className="max-w-full overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-[1640px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                  <th className="py-3 px-4">CLIENTE</th>
                  <th className="py-3 px-4">DNI</th>
                  <th className="py-3 px-4">N° CUOTA</th>
                  <th className="py-3 px-4">VENCIMIENTO</th>
                  <th className="py-3 px-4">F. PAGO</th>
                  <th className="py-3 px-4">CAPITAL</th>
                  <th className="py-3 px-4">INTERÉS</th>
                  <th className="py-3 px-4">DÍAS MORA</th>
                  <th className="py-3 px-4">MORA</th>
                  <th className="py-3 px-4">OTROS</th>
                  <th className="py-3 px-4">CUOTA</th>
                  <th className="py-3 px-4">SALDO PENDIENTE</th>
                  <th className="py-3 px-4">ESTADO</th>
                  <th className="py-3 px-4">ASESOR</th>
                  <th className="py-3 px-4">COMPROBANTE</th>
                  <th className="py-3 px-4">OBSERVACIONES</th>
                </tr>
              </thead>
              <tbody>
                {cuotas.map(cuota => (
                  <tr
                    key={cuota.idCuota}
                    className="border-b border-slate-200 transition hover:bg-slate-50"
                  >
                    <td className="py-3 px-4">{cuota.cliente_nombre}</td>
                    <td className="py-3 px-4">{cuota.cliente_dni}</td>
                    <td className="py-3 px-4">{cuota.numero_cuota}</td>
                    <td className="py-3 px-4">{formatDate(cuota.fecha_vencimiento)}</td>
                    <td className="py-3 px-4">{formatPaymentDate(cuota)}</td>
                    <td className="py-3 px-4">{parseFloat(cuota.capital).toFixed(2)}</td>
                    <td className="py-3 px-4">{parseFloat(cuota.interes).toFixed(2)}</td>
                    <td className="py-3 px-4">{cuota.dias_mora}</td>
                    <td className="py-3 px-4">
                      {(() => {
                        const mora = Number(cuota.mora) || 0;
                        const red = Number(cuota.mora_reducida) || 0;
                        const pct = Number.isFinite(Number(cuota.mora_reducida_pct))
                          ? Number(cuota.mora_reducida_pct)
                          : ((mora + red) > 0 ? Math.round((red / (mora + red)) * 100) : 0);

                        return (
                          <>
                            <span>S/ {mora.toFixed(2)}</span>
                            {red > 0 && (
                              <span
                                className="ml-2 inline-flex items-center gap-1 text-xs text-green-600 whitespace-nowrap"
                                title="Reducción de mora"
                              >
                                <span aria-hidden="true">↓</span>
                                <span>S/ {red.toFixed(2)}{pct ? ` (${pct}%)` : ''}</span>
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td className="py-3 px-4">{parseFloat(cuota.otros).toFixed(2)}</td>
                    <td className="py-3 px-4">{parseFloat(cuota.monto).toFixed(2)}</td>
                    <td className="py-3 px-4 font-semibold">{toNumber(cuota.saldo_pendiente).toFixed(2)}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${getStatusStyles(
                          cuota.estado
                        )}`}
                      >
                        {cuota.estado}
                      </span>
                    </td>
                    <td className="py-3 px-4">{cuota.asesor_nombre}</td>
                    <td className="py-3 px-2 text-center">
                      {cuota.estado === 'pagado' && (
                        <button
                          onClick={() => handleViewComprobante(cuota.idCuota)}
                          className="rounded-lg bg-red-600 p-2 text-white transition hover:bg-red-700"
                          aria-label="Ver comprobante de pago"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        </button>
                      )}
                    </td>
                    <td className="py-3 px-4">{cuota.observaciones || '-'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50 font-semibold text-slate-700">
                  <td colSpan="3" className="py-3 px-4">
                    TOTALES
                  </td>
                  <td className="py-3 px-4"></td>
                  <td className="py-3 px-4"></td>
                  <td className="py-3 px-4">
                    {cuotas.reduce((sum, cuota) => sum + parseFloat(cuota.capital), 0).toFixed(2)}
                  </td>
                  <td className="py-3 px-4">
                    {cuotas.reduce((sum, cuota) => sum + parseFloat(cuota.interes), 0).toFixed(2)}
                  </td>
                  <td className="py-3 px-4">
                    {cuotas.reduce((sum, cuota) => sum + parseFloat(cuota.dias_mora || 0), 0)}
                  </td>
                  <td className="py-3 px-4"></td>
                  <td className="py-3 px-4">
                    {cuotas.reduce((sum, cuota) => sum + parseFloat(cuota.otros), 0).toFixed(2)}
                  </td>
                  <td className="py-3 px-4">
                    {cuotas.reduce((sum, cuota) => sum + parseFloat(cuota.monto), 0).toFixed(2)}
                  </td>
                  <td className="py-3 px-4">
                    {cuotas.reduce((sum, cuota) => sum + toNumber(cuota.saldo_pendiente), 0).toFixed(2)}
                  </td>
                  <td className="py-3 px-4"></td>
                  <td className="py-3 px-4"></td>
                  <td className="py-3 px-4"></td>
                  <td className="py-3 px-4"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

const formatDate = (dateString) => {
  try {
    if (!dateString) return '-';
    return format(parseISO(dateString), 'dd/MM/yyyy');
  } catch (error) {
    console.error('Error formatting date:', dateString, error);
    return '-';
  }
};

const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

export default FiltrarPrestamos;